import type { Card as CardType } from '../poker/types';
import { isRedSuit } from '../poker/gameLogic';
import { cn } from '@/lib/utils';

interface CardProps {
  card: CardType;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  highlighted?: boolean;
}

export function Card({ card, size = 'md', className, highlighted = false }: CardProps) {
  const isRed = isRedSuit(card.suit);
  
  const sizeClasses = {
    xs: 'w-8 h-11 sm:w-10 sm:h-14 text-xs',
    sm: 'w-12 h-16 sm:w-16 sm:h-22 text-sm sm:text-base',
    md: 'w-16 h-22 sm:w-20 sm:h-28 md:w-24 md:h-32 text-base sm:text-xl',
    lg: 'w-20 h-28 sm:w-24 sm:h-32 md:w-28 md:h-38 text-xl sm:text-2xl',
  };
  
  if (card.hidden) {
    return (
      <div 
        className={cn(
          sizeClasses[size],
          'relative rounded-xl shadow-xl overflow-hidden bg-zinc-800',
          'transition-all duration-500 ease-out',
          className
        )}
      >
        <img 
          src="/card.png" 
          alt="Carta girada" 
          className="w-full h-full object-contain"
        />
      </div>
    );
  }
  
  return (
    <div 
      className={cn(
        sizeClasses[size],
        'relative rounded-xl bg-white shadow-xl',
        'transition-all duration-300 ease-out',
        highlighted && 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-zinc-950',
        className
      )}
    >
      {/* Número i símbol sempre a dalt esquerra */}
      <div className={cn(
        'absolute top-1.5 left-1.5 flex flex-col items-start gap-0',
        'font-black leading-none',
        isRed ? 'text-red-500' : 'text-zinc-800'
      )}>
        <span className="text-[1.5em]">{card.rank}</span>
        <span className="text-[1.2em] -mt-0.5">{card.suit}</span>
      </div>
    </div>
  );
}

interface CardStackProps {
  cards: CardType[];
  overlap?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  highlighted?: boolean;
}

export function CardStack({ cards, overlap = true, size = 'md', highlighted = false }: CardStackProps) {
  const overlapClasses = {
    xs: '-space-x-3',
    sm: '-space-x-5',
    md: '-space-x-6',
    lg: '-space-x-8',
  };
  
  return (
    <div className={cn('flex items-center', overlap ? overlapClasses[size] : 'gap-2')}>
      {cards.map((card, index) => (
        <Card 
          key={`${card.rank}-${card.suit}-${index}`} 
          card={card} 
          size={size}
          highlighted={highlighted}
          className={cn(
            'transition-all duration-500 ease-out',
            overlap && 'hover:-translate-y-2 hover:z-20 hover:scale-105'
          )}
        />
      ))}
    </div>
  );
}
